import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';

const VAT_RATE = 0.20;

// Roboto, AdminOfferRequests generatePDF içinde ensureRobotoFontForPdf() ile aynı origin'den yüklenir (Türkçe karakter desteği).

const numberFmt = (n) =>
  (Number.isFinite(parseFloat(n)) ? parseFloat(n) : 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Roboto',
    fontSize: 10,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  leftCol: {
    width: '45%',
  },
  rightCol: {
    width: '45%',
  },
  companyName: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  line: {
    marginBottom: 3,
    fontSize: 9,
  },
  mainTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#666',
    color: 'white',
    fontWeight: 'bold',
    padding: 6,
    fontSize: 9,
  },
  tableRow: {
    flexDirection: 'row',
    padding: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    fontSize: 8,
  },
  tableRowStriped: {
    flexDirection: 'row',
    padding: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    backgroundColor: '#f9f9f9',
    fontSize: 8,
  },
  col1: { width: '5%' },
  col2: { width: '20%' },
  col3: { width: '30%' },
  col4: { width: '10%' },
  col5: { width: '15%' },
  col6: { width: '15%' },
  summaryHeader: {
    flexDirection: 'row',
    backgroundColor: '#e6e6e6',
    fontWeight: 'bold',
    padding: 6,
    marginTop: 12,
    fontSize: 9,
  },
  summaryRow: {
    flexDirection: 'row',
    padding: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    fontSize: 9,
  },
  notesTitle: {
    fontWeight: 'bold',
    marginTop: 20,
    marginBottom: 4,
  },
  notesText: {
    fontSize: 9,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 8,
  },
  logo: {
    width: 120,
    height: 28,
    marginBottom: 6,
  },
  pkgBlock: {
    marginTop: 12,
    marginBottom: 8,
  },
  pkgName: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  pkgDesc: {
    fontSize: 9,
    marginBottom: 4,
  },
  pkgPrice: {
    fontSize: 9,
    fontWeight: 'bold',
    marginTop: 4,
  },
  featureTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#666',
    color: 'white',
    fontWeight: 'bold',
    padding: 4,
    fontSize: 8,
    marginTop: 4,
  },
  featureTableRow: {
    flexDirection: 'row',
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    fontSize: 8,
  },
});

function OfferPDFDocument({ offer, companyInfo = {}, logoUrl }) {
  const companyName = companyInfo.company_name || 'Website Al';
  const companyEmail = companyInfo.email || 'info@website-al.com';
  const companyPhone = companyInfo.phone || '';
  const companyAddress = companyInfo.address || '';
  const companyWebsite = companyInfo.website || '';

  const services = Array.isArray(offer.offer_data?.services) ? offer.offer_data.services : [];
  const packages = Array.isArray(offer.offer_data?.custom_packages) ? offer.offer_data.custom_packages : [];
  const servicesTotal = services.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
  const packagesTotal = packages.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
  const subtotal = servicesTotal + packagesTotal;
  const vatAmount = subtotal * VAT_RATE;
  const total = subtotal + vatAmount;

  const dateStr = new Date().toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const fullLogoUrl = logoUrl
    ? logoUrl.startsWith('http')
      ? logoUrl
      : `${typeof window !== 'undefined' ? window.location.origin : ''}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`
    : null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Üst: Sol firma, sağ müşteri */}
        <View style={styles.row}>
          <View style={styles.leftCol}>
            {fullLogoUrl && (
              <Image src={fullLogoUrl} style={styles.logo} />
            )}
            <Text style={styles.companyName}>{companyName}</Text>
            {companyAddress ? <Text style={styles.line}>{companyAddress}</Text> : null}
            {companyEmail ? <Text style={styles.line}>E-posta: {companyEmail}</Text> : null}
            {companyPhone ? <Text style={styles.line}>Telefon: {companyPhone}</Text> : null}
            <Text style={styles.line}>Tarih: {dateStr}</Text>
            {companyWebsite ? <Text style={styles.line}>Web: {companyWebsite}</Text> : null}
          </View>
          <View style={styles.rightCol}>
            <Text style={styles.sectionTitle}>Müşteri Bilgileri</Text>
            {offer.company_name?.trim() ? <Text style={styles.line}>Şirket: {offer.company_name}</Text> : null}
            {offer.contact_name?.trim() ? <Text style={styles.line}>İletişim: {offer.contact_name}</Text> : null}
            {offer.email?.trim() ? <Text style={styles.line}>E-posta: {offer.email}</Text> : null}
            {offer.phone?.trim() ? <Text style={styles.line}>Telefon: {offer.phone}</Text> : null}
            {offer.address?.trim() ? <Text style={styles.line}>Adres: {offer.address}</Text> : null}
            {offer.tax_number?.trim() ? <Text style={styles.line}>Vergi No: {offer.tax_number}</Text> : null}
          </View>
        </View>

        {/* Teklif başlığı */}
        <Text style={styles.mainTitle}>{offer.offer_title || 'Teklif'}</Text>

        {/* Hizmetler tablosu */}
        {services.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Hizmetler</Text>
            <View style={styles.tableHeader}>
              <Text style={styles.col1}>#</Text>
              <Text style={styles.col2}>Hizmet Adı</Text>
              <Text style={styles.col3}>Açıklama</Text>
              <Text style={styles.col4}>Miktar</Text>
              <Text style={styles.col5}>Birim Fiyat</Text>
              <Text style={styles.col6}>Toplam</Text>
            </View>
            {services.map((s, i) => (
              <View key={i} style={i % 2 === 0 ? styles.tableRow : styles.tableRowStriped}>
                <Text style={styles.col1}>{i + 1}</Text>
                <Text style={styles.col2}>{s.name || ''}</Text>
                <Text style={styles.col3}>{s.description || ''}</Text>
                <Text style={styles.col4}>{s.quantity}</Text>
                <Text style={styles.col5}>{numberFmt(s.unit_price)} TL</Text>
                <Text style={styles.col6}>{numberFmt(s.total)} TL</Text>
              </View>
            ))}
          </View>
        )}

        {/* Özel paketler */}
        {packages.length > 0 && (
          <View>
            <Text style={[styles.sectionTitle, { marginTop: 14 }]}>Özel Paketler</Text>
            {packages.map((pkg, idx) => (
              <View key={idx} style={styles.pkgBlock}>
                <Text style={styles.pkgName}>{idx + 1}. {pkg.name || ''}</Text>
                {pkg.description ? <Text style={styles.pkgDesc}>{pkg.description}</Text> : null}
                {Array.isArray(pkg.features) && pkg.features.filter(Boolean).length > 0 && (
                  <View>
                    <View style={styles.featureTableHeader}>
                      <Text>Özellik</Text>
                    </View>
                    {pkg.features.filter(Boolean).map((f, i) => (
                      <View key={i} style={styles.featureTableRow}>
                        <Text>{String(f)}</Text>
                      </View>
                    ))}
                  </View>
                )}
                <Text style={styles.pkgPrice}>Fiyat: {numberFmt(pkg.price)} TL</Text>
              </View>
            ))}
          </View>
        )}

        {/* Özet */}
        <View style={styles.summaryHeader}>
          <Text style={{ width: '70%' }}>Kalem</Text>
          <Text style={{ width: '30%' }}>Tutar</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={{ width: '70%' }}>Ara Toplam (KDV hariç)</Text>
          <Text style={{ width: '30%' }}>{numberFmt(subtotal)} TL</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={{ width: '70%' }}>KDV (%{(VAT_RATE * 100).toFixed(0)})</Text>
          <Text style={{ width: '30%' }}>{numberFmt(vatAmount)} TL</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={{ width: '70%', fontWeight: 'bold' }}>Genel Toplam (KDV dahil)</Text>
          <Text style={{ width: '30%', fontWeight: 'bold' }}>{numberFmt(total)} TL</Text>
        </View>

        {/* Notlar */}
        {offer.notes?.trim() && (
          <View style={{ marginTop: 20 }}>
            <Text style={styles.notesTitle}>Notlar:</Text>
            <Text style={styles.notesText}>{offer.notes}</Text>
          </View>
        )}

        <Text style={styles.footer} fixed>
          Sayfa 1 / 1
        </Text>
      </Page>
    </Document>
  );
}

export default OfferPDFDocument;
export { numberFmt, VAT_RATE };
